import { useRef } from 'react';
import { FieldPath, SubmitHandler } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { associateContact } from 'api/contacts/companies';
import { createContact } from 'api/contacts/single';
import * as ContactForm from 'assets/js/pages/contacts/ContactForm';
import * as CompanyForm from 'assets/js/pages/contacts/companies/CompanyForm';
import { transformServerKeyIntoFormKey } from 'assets/js/pages/contacts/hooks/useSaveContact';
import { ContactFormValues } from 'assets/js/pages/contacts/types';
import { transformAddressFormToRequest } from 'components/address-form/useAccountAddressForm';
import { useDialogRoute } from 'components/dialog-route/DialogRouteContext';
import * as Drawer from 'components/drawer';
import * as Form from 'components/form';
import { useGivebutterForm } from 'components/form';
import { ContactType } from 'constants/contacts';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentContact } from 'customHooks/useCurrentContact';
import { useFormServerErrors } from 'customHooks/useFormServerErrors';
import { accountQueryKeys } from 'queries/account-query-keys';
import { ContactEmail } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

const AddCompanyDrawer = () => {
    const { accountId } = useCurrentAccount();
    const { data: contact } = useCurrentContact();
    const toast = useToast();
    const queryClient = useQueryClient();
    const { goBack } = useDialogRoute();

    const methods = useGivebutterForm<ContactFormValues>({
        defaultValues: {
            selectedCompany: [],
            companyName: '',
            companyEmails: [{ value: '', isPrimary: true, type: 'work' }],
            companyPhones: [{ value: '', isPrimary: true, type: 'work' }],
            addresses: [
                {
                    line1: '',
                    line2: '',
                    city: '',
                    zip: '',
                    stateUs: '',
                    stateIntl: '',
                    country: 'USA',
                    isPrimary: true,
                },
            ],
        },
    });

    const { setAddressErrors } = useFormServerErrors(methods);
    const { handleSubmit, watch, setError, resetFormButton } = methods;
    const selectedCompany = watch('selectedCompany');
    const ref = useRef<HTMLDivElement>(null);

    const { mutateAsync: create, isPending: isCreating } = useMutation({
        mutationFn: (value: ContactFormValues) => {
            const data = {
                companyName: value.companyName,
                emails: value.companyEmails
                    .filter(item => item.value)
                    .map(
                        item =>
                            ({
                                value: item.value,
                                is_primary: item.isPrimary,
                                type: item.type,
                            }) as ContactEmail,
                    ),
                phones: value.companyPhones
                    .filter(item => item.value)
                    .map(item => ({
                        value: item.value,
                        is_primary: item.isPrimary,
                        type: item.type,
                    })),
                addresses: value.addresses
                    .filter(address => address.line1.length > 0)
                    .map(transformAddressFormToRequest),
                type: ContactType.Company,
            };
            return createContact(accountId, data);
        },
        onSuccess: ({ data }) => {
            associate(data.id);
        },
        onError: (error: GivebutterAxiosError) => {
            const errors = error.response?.data.errors;
            if (errors) {
                Object.keys(errors).forEach(key => {
                    const normalizedKey = transformServerKeyIntoFormKey(
                        key as Parameters<typeof transformServerKeyIntoFormKey>[0],
                    );

                    if (normalizedKey?.includes('addresses')) {
                        setAddressErrors(errors);
                        return;
                    }

                    if (normalizedKey) {
                        setError(normalizedKey as FieldPath<ContactFormValues>, {
                            message: errors[key][0],
                        });
                    }
                });
            }
            toast.error(
                error.response?.data.message ?? `Failed to create contact. Please try again.`,
            );
            resetFormButton();
        },
    });

    const { mutateAsync: associate, isPending: isAssociating } = useMutation({
        mutationFn: async (companyId: number) => {
            await associateContact(accountId, companyId, { contact_id: contact.id });
            return companyId;
        },
        onSuccess: companyId => {
            toast.success('Successfully added an associated company');
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.contactById(accountId, contact.id),
            });
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.companyContacts(accountId, companyId),
                type: 'all',
            });
            goBack();
        },
        onError: (error: GivebutterAxiosError) => {
            toast.error(error.response?.data.message ?? 'Failed to associate contact.');
        },
    });

    const submit = (value: ContactFormValues) => {
        const selectedCompanyId = value.selectedCompany?.[0]?.id;
        if (selectedCompanyId !== ContactForm.NewContactId) {
            associate(parseInt(selectedCompanyId));
        } else {
            create(value);
        }
    };

    const isPending = isCreating || isAssociating;

    return (
        <Form.Provider {...methods}>
            <form
                className={'contents'}
                onSubmit={handleSubmit(submit as SubmitHandler<ContactFormValues>)}
            >
                <Drawer.Header>
                    <Drawer.Title>Add an associated company</Drawer.Title>
                    <Drawer.Close />
                </Drawer.Header>
                <Drawer.Body ref={ref}>
                    <ContactForm.Container>
                        <CompanyForm.SelectForm showAddress />
                    </ContactForm.Container>
                </Drawer.Body>
                <Drawer.Footer>
                    <div
                        className={
                            'flex w-full flex-wrap-reverse items-center justify-between gap-2'
                        }
                    >
                        <Drawer.Cancel asChild={true}>
                            <Button variant={'transparent'}>Cancel</Button>
                        </Drawer.Cancel>
                        <Form.SubmitButton
                            disabled={!selectedCompany?.[0]?.id}
                            isSubmitting={isPending}
                        >
                            Add company
                        </Form.SubmitButton>
                    </div>
                </Drawer.Footer>
            </form>
        </Form.Provider>
    );
};

const AddCompanyDrawerTrigger = Drawer.Trigger;

const Root = AddCompanyDrawer;
const Trigger = AddCompanyDrawerTrigger;

export {
    AddCompanyDrawer,
    AddCompanyDrawerTrigger,
    //
    Root,
    Trigger,
};
